// Extra CMS slots for the 'founding_venue_offer' page — added to make previously-hardcoded
// public marketing copy editable. Concatenated into CMS_SLOTS via ./index.
// Each default MUST equal the exact copy the component rendered before wiring,
// so the public page is byte-identical until an admin edits it.
import type { SlotDef } from '../slots';

export const foundingVenueOfferExtra: SlotDef[] = [
  // ─── Section headers (eyebrow + title for each of the three sections) ────
  {
    key: 'founding_venue_offer.offer_eyebrow',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Offer section: eyebrow',
    default: 'The offer',
  },
  {
    key: 'founding_venue_offer.offer_title',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Offer section: title',
    default: 'What you get.',
  },
  {
    key: 'founding_venue_offer.math_eyebrow',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Math section: eyebrow',
    default: 'The math',
  },
  {
    key: 'founding_venue_offer.math_title',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Math section: title',
    default: 'Plain numbers, no asterisks.',
  },
  {
    key: 'founding_venue_offer.reciprocity_eyebrow',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Reciprocity section: eyebrow',
    default: 'Reciprocity',
  },
  {
    key: 'founding_venue_offer.reciprocity_title',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Reciprocity section: title',
    default: 'What we ask in return.',
  },
  // ─── Offer lines (headline + supporting sentence, paired per item) ───────
  // Replaces the old `offer_lines` bullet_list, whose headlines were index-
  // zipped against a hardcoded body array (mis-paired on reorder). Each item
  // now carries its own headline (title) + supporting sentence (body).
  {
    key: 'founding_venue_offer.offer_items',
    page: 'founding_venue_offer',
    kind: 'step_list',
    label: 'Offer lines',
    helpText:
      'Each entry is one numbered offer line: title is the headline, body is the supporting sentence. The "01 / 02 …" number is set by position.',
    default: [
      {
        title: 'One year free. No credit card up front.',
        body: 'No credit card up front. We want you to use it for a full year before you pay for it.',
      },
      {
        title: 'Founding rate locked for life. It won’t go up.',
        body: "The founding rate doesn't go up. As long as you're a member, you keep this price.",
      },
      {
        title: 'Permanent Founding Venue mark on your profile.',
        body: 'Visible to every artist who finds you, for as long as you host.',
      },
      {
        title: 'Hand-onboarding from the WWAAVE team.',
        body: 'We help you photograph the space, write the description, and answer the first few requests.',
      },
    ],
  },
  // ─── The math section body ───────────────────────────────────────────────
  {
    key: 'founding_venue_offer.math_promise',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Math: core promise paragraph',
    default:
      "No cut of your stipends. No fees on the exchange. No paid placement. WWAAVE doesn't take a cut of any payment between you and an artist. The subscription is the whole price.",
  },
  {
    key: 'founding_venue_offer.referral_callout',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Math: referral-incentive callout',
    default: '10% of your subscription goes back to the artist who introduced you to WWAAVE.',
  },
  // ─── Price-tile captions (the dollar figures stay live/dynamic) ──────────
  {
    key: 'founding_venue_offer.price_caption_year_one',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Price tile 1: caption ($0)',
    helpText: 'The "$0" figure is fixed in code; this is the caption under it.',
    default: 'First full year.',
  },
  {
    key: 'founding_venue_offer.price_caption_monthly',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Price tile 2: caption (monthly)',
    helpText: 'The monthly dollar figure above the caption is live from the pricing setting.',
    default: 'Per month after, locked for life.',
  },
  {
    key: 'founding_venue_offer.price_caption_yearly_saves',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Price tile 3 caption (shown when annual saves money)',
    helpText:
      'Shown when annual billing saves money. Use {save} where the savings percentage should appear. The yearly dollar figure above is live from the pricing setting.',
    default: 'Per year, if you switch to annual (saves {save}%).',
  },
  {
    key: 'founding_venue_offer.price_caption_yearly_plain',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Price tile 3 caption (shown when annual does NOT save)',
    helpText: 'Shown when annual billing does not save money.',
    default: 'Per year, if you switch to annual.',
  },
  // ─── Reciprocity list ("What we ask in return") ──────────────────────────
  {
    key: 'founding_venue_offer.reciprocity_list',
    page: 'founding_venue_offer',
    kind: 'bullet_list',
    label: 'What we ask in return (one per line)',
    default: [
      "Verify your account so artists can see who they're writing to.",
      'Reply to requests within a week, even when the answer is "not this time."',
      "Tell the truth about your space. Capacity, sleeping arrangement, what you offer, what you don't.",
      'Honour the promise. No on-platform money, no off-platform pressure.',
    ],
  },
  // ─── CTA row (button labels, promise link, fallback sentences) ───────────
  {
    key: 'founding_venue_offer.cta_button_default',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'CTA button: default label',
    helpText: 'Shown while founding spots remain. Links to sign-up as a host (fixed).',
    default: 'Sign up as a host',
  },
  {
    key: 'founding_venue_offer.cta_button_claimed',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'CTA button: fully-claimed label',
    helpText: 'Shown once all founding spots are claimed. Links to sign-up as a host (fixed).',
    default: 'Join the waitlist',
  },
  {
    key: 'founding_venue_offer.cta_promise_link',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'CTA: secondary link label',
    helpText: 'Links to the Promise page (fixed). Include the arrow if you want it.',
    default: 'Read the promise →',
    // Removed from the page (feedback ZUpiVILvIUfpok1OJGKsx) — the Promise page
    // already appears in the "Before you sign up" tiles, so this secondary link
    // after the CTA was a duplicate. Kept as a deprecated row so any existing
    // override doesn't orphan.
    deprecated: true,
  },
  {
    key: 'founding_venue_offer.cta_claimed_note',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'CTA: fully-claimed note (hidden until all spots are taken)',
    helpText: 'Shown above the button once the cohort is full. Use {cap} for the live cohort size.',
    default:
      "All {cap} founding spots are claimed. Join the waitlist and we'll reach out when room opens.",
  },
  {
    key: 'founding_venue_offer.cta_standard_note',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'CTA: standard-plan fallback note',
    helpText:
      'Shown below the button while spots remain. Use {monthly} for the live monthly price (e.g. "$19 AUD").',
    default:
      "If we're full when you read this, the standard plan ({monthly}/month from day one) is open and looks the same in every other way.",
    // Removed from the page (PlQ4H feedback) — the fully-claimed note above
    // already covers what happens once spots run out. Kept as a deprecated
    // row so any existing override doesn't orphan.
    deprecated: true,
  },
  // ─── Closing "Before you sign up" tile block ─────────────────────────────
  {
    key: 'founding_venue_offer.next_steps_title',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Before you sign up: title',
    default: 'Before you sign up.',
  },
  {
    key: 'founding_venue_offer.next_steps_lede',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Before you sign up: lede',
    default: 'Two pages worth reading first: the principles, and the everyday flow.',
    // Closing-block subheading removed site-wide at Richie's request — no longer
    // rendered. Retired (not deleted) so any override doesn't orphan; drops out
    // of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'founding_venue_offer.next_step_1_title',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Before you sign up: tile 1 title',
    helpText: 'Links to the Promise page (fixed). Only the title/description are editable.',
    default: 'The WWAAVE Promise',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'founding_venue_offer.next_step_1_desc',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Before you sign up: tile 1 description',
    default: 'No cuts, no premium-placement paywalls, no data sales.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'founding_venue_offer.next_step_2_title',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Before you sign up: tile 2 title',
    helpText: 'Links to the How it works page (fixed). Only the title/description are editable.',
    default: 'How it works',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'founding_venue_offer.next_step_2_desc',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Before you sign up: tile 2 description',
    default: 'Sign-up to first stay, end to end, both sides.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  // ─── Hero eyebrow (cohort-status branches) ───────────────────────────────
  // The page picks one of these four depending on live cohort data (full /
  // closing soon / still loading / normal count). Renders in ALL CAPS via
  // CSS, so write the defaults in normal sentence case.
  {
    key: 'founding_venue_offer.eyebrow_default',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Hero eyebrow (normal: spots remaining)',
    helpText:
      'Shown once cohort data has loaded, the cohort is not full or closing soon, AND claimed spots have cleared the reveal threshold (20). Use {remaining} for the live spots-left count and {cap} for the cohort size. Renders in ALL CAPS automatically.',
    default: 'Founding cohort · {remaining} of {cap} spots remaining',
  },
  {
    key: 'founding_venue_offer.eyebrow_open',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Hero eyebrow (open, count not yet shown)',
    helpText:
      'Shown once cohort data has loaded but claimed spots are still below the reveal threshold (20) — a cohort in single digits reads as "did this launch fail?" rather than "spots are going". Use {cap} for the cohort size. Renders in ALL CAPS automatically.',
    default: 'Founding cohort · Now open',
  },
  {
    key: 'founding_venue_offer.eyebrow_closing_soon',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Hero eyebrow (closing soon: fewer than 10 spots left)',
    helpText:
      'Shown when fewer than 10 spots remain. Use {remaining} for the live spots-left count and {spot_word} for "spot" (singular) or "spots" (plural), chosen automatically. Renders in ALL CAPS automatically.',
    default: 'Founding cohort · {remaining} {spot_word} remaining',
  },
  {
    key: 'founding_venue_offer.eyebrow_full',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Hero eyebrow (fully claimed)',
    helpText:
      'Shown once every founding spot is claimed. Use {cap} for the live cohort size. Renders in ALL CAPS automatically.',
    default: 'Founding cohort · {cap} of {cap} claimed',
  },
  {
    key: 'founding_venue_offer.eyebrow_loading',
    page: 'founding_venue_offer',
    kind: 'markdown',
    label: 'Hero eyebrow (loading, cohort data not yet in)',
    helpText:
      'Shown briefly before cohort data has loaded. Use {cap} for the cohort size. Renders in ALL CAPS automatically.',
    default: 'Founding cohort · {cap} spots',
  },
];
